import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import ErrorModal from '../../shared/components/UIElements/ErrorModal';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';

import { AuthContext } from '../../shared/context/auth-context';
import { useHttpClient } from '../../shared/hooks/http-hook';
import ReactPlayer from 'react-player';
import SentimentList from '../components/SentimentList';
import EvaluationCard from '../components/EvaluationCard';
import Button from '../../shared/components/FormElements/Button/Button';
import './EvaluationReport.css';

const EvaluationReport = props => {
    const evaluationId = useParams().evaluationId;
    const auth = useContext(AuthContext);
    const { isLoading, error, sendRequest, clearError } = useHttpClient();
    const [evaluation, setEvaluation] = useState();
    const [question, setQuestion] = useState();
    const [sentimentAnalysis, setSentimentAnalysis] = useState();
    const [isReady, setIsReady] = useState(false);
    const playerRef = useRef();

    const onReady = useCallback(() => {
        if (!isReady) {
          const timeToStart = 0;
          playerRef.current.seekTo(timeToStart, "seconds");
          setIsReady(true);
        }
      }, [isReady]);

    useEffect(()=>{
        const fetchEvaluation = async () => {
            const responseData = await sendRequest(process.env.REACT_APP_BACKEND_URL_PLAN+`/evaluation/${evaluationId}`, 'GET', null, {Authorization:'Bearer '+ auth.token});
            setEvaluation(responseData.evaluation);
            setQuestion(responseData.question);

            if (responseData.evaluation.sentiment_analysis) {
                responseData.evaluation.sentiment_analysis.forEach(function(analysis, i){
                    analysis.id = i+1;
                })
                console.log(responseData.evaluation.sentiment_analysis);
                setSentimentAnalysis(responseData.evaluation.sentiment_analysis);
            }
            else {
                setSentimentAnalysis([]);
            }
        };

        fetchEvaluation();
    },[sendRequest, evaluationId, auth.token]);

    const clickSentimentHandler = (start) => {
        console.log(start/1000)
        playerRef.current.seekTo(start/1000, "seconds");
    }

    return (
        <React.Fragment>
        <ErrorModal error={error} onClear={clearError} />
        {isLoading && <LoadingSpinner asOverlay />}
        {!isLoading && question && evaluation && sentimentAnalysis && 
        <div className='evaluation-container'>
        <div className='left-container'>
        <div className='left-content'>
        <motion.div 
            className='back-to-q-button'
            initial={{ opacity: 0, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0, duration: 0.75 }}
        >
            <Button to={`/question/evaluation/${evaluation.questionId}`} transparent_100><p style={{ letterSpacing: '0.2rem' }}>{`<BACK`}</p></Button>
        </motion.div>
        <motion.div
            initial={{ opacity: 1, x: -1000 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0, duration: 0.75 }}
        >
        
        <EvaluationCard 
            key={evaluation.emotion} 
            title="Emotion" emotion={evaluation.emotion}
        />
        </motion.div>
        <motion.div
            initial={{ opacity: 1, x: -1000 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25, duration: 0.75 }}
        >
        <EvaluationCard 
            key={evaluation.overall.clarity.comments} 
            title="Clarity" score={evaluation.overall.clarity.score} 
            comments={evaluation.overall.clarity.comments} 
            improvements={evaluation.overall.clarity.improvements} 
        />
        </motion.div>
        <motion.div
            initial={{ opacity: 1, x: -1000 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.75 }}
        >
        <EvaluationCard 
            key={evaluation.overall.confidence.comments} 
            title="Confidence" score={evaluation.overall.confidence.score} 
            comments={evaluation.overall.confidence.comments} 
            improvements={evaluation.overall.confidence.improvements} 
        />
        </motion.div>
        <motion.div
            initial={{ opacity: 1, x: -1000 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.75, duration: 0.75 }}
        >
        <EvaluationCard 
            key={evaluation.overall.relevance.comments} 
            title="Relevance" score={evaluation.overall.relevance.score} 
            comments={evaluation.overall.relevance.comments} 
            improvements={evaluation.overall.relevance.improvements} 
        />
        </motion.div>
        <motion.div
            initial={{ opacity: 1, x: -1000 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, duration: 0.75 }}
        >
        <EvaluationCard 
            key={evaluation.overall.job_fit.comments} 
            title="Job Fit" score={evaluation.overall.job_fit.score} 
            comments={evaluation.overall.job_fit.comments} 
            improvements={evaluation.overall.job_fit.improvements} 
        />
        </motion.div>
        <motion.div
            initial={{ opacity: 1, x: -1000 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.25, duration: 0.75 }}
        >
        <EvaluationCard 
            key={evaluation._id} 
            title="Filler Count" filler_count={evaluation.filler_count}
        />
        </motion.div>
        </div>
        </div>
        <div className='right-container'>
            <div className='right-content' >
                <motion.div
                    initial={{ opacity: 1, x: 1000 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0, duration: 0.75 }}
                >
                    <h2>{question.question}</h2>
                    <h3>Your Answer:</h3>
                </motion.div>
                <div>
                    <motion.div
                        className='video-wrapper'
                        initial={{ opacity: 1, x: 1000 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.625, duration: 0.75 }}
                    >
                        <ReactPlayer
                        ref={playerRef}
                        url={`${evaluation.video}`}
                        playing={false}
                        width="100%"
                        height="100%"
                        border-radius='20px'
                        controls={true}
                        onReady={onReady}
                        />
                    </motion.div>
                </div>
                <motion.div
                    initial={{ opacity: 1, x: 1000 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.25, duration: 0.75 }}
                >
                <h3>Transcript:</h3>
                <SentimentList items={sentimentAnalysis} onClickSentiment={clickSentimentHandler} />
                </motion.div>
            </div>
        </div>
    </div>}
        </React.Fragment>
    );

};

export default EvaluationReport;